import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import api, { ImportColumnsSuggestionDto, ImportProductsArgsAction, ImportRetailerProductsResultDto, RetailerDto } from "../../../api";
import { UploadFileControl, UploadedFileInfo } from "../../controls";
import { ColumnList } from "../../controls/ColumnList";
import { ColumnMultiList } from "../../controls/ColumnMultiList";
import { firstLetterUppercase, isExcel, notif } from "../../utils";

export type RetailerProductsImportDialogAction = "none" | "import";

export interface RetailerProductsImportDialogProps {
  action: RetailerProductsImportDialogAction;
  retailer: RetailerDto | null;
  onClose?: () => void;
  onImport?: (result: ImportRetailerProductsResultDto) => void;
}

export const RetailerProductsImportDialog = (props: RetailerProductsImportDialogProps) => {
  const { retailer, action, onClose, onImport } = props;

  const [open, setOpen] = useState(false);
  const [originalName, setOriginalName] = useState("");
  const [fileName, setFileName] = useState("");
  const [exclude, setExclude] = useState(false);
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState("");
  const [headerRow, setHeaderRow] = useState("1");
  const [dataRow, setDataRow] = useState("2");
  const [columns, setColumns] = useState<string[]>([]);
  const [columnSKU, setColumnSKU] = useState("");
  const [tokenColumns, setTokenColumns] = useState<string[]>([]);
  const [importAction, setImportAction] = useState<ImportProductsArgsAction>(ImportProductsArgsAction.None);

  const handleAccept = async () => {
    try {
      if (retailer?.id && fileName) {
        if (action === "import") {
          const result = await api.retailerProduct.importProducts({
            fileName: fileName,
            retailerId: retailer.id,
            exclude,
            columnSKU: columnSKU,
            tokenColumns: tokenColumns,
            sheet,
            headerRow,
            dataRow,
          } as any);
          setOpen(false);
          if (onImport) onImport(result);
        } else {
          notif(`Unsupported action ${action}`, "error");
        }
      } else {
        notif("Vendor not selected.", "error");
      }
    } catch (err) {
      notif(err, "error");
    }
  };
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const loadColumns = useCallback(async () => {
    if (fileName) {
      try {
        let result: string[] = [];
        let suggestion: ImportColumnsSuggestionDto;
        if (isExcel(fileName) && sheet && headerRow) {
          result = await api.file.columns(fileName, sheet, parseInt(headerRow));
          suggestion = await api.file.suggestion(fileName, sheet, parseInt(headerRow));
        } else {
          result = await api.file.columns(fileName, undefined, undefined);
          suggestion = await api.file.suggestion(fileName, undefined, undefined);
        }
        setColumnSKU(suggestion?.columnSKU || "");
        setTokenColumns(suggestion?.tokenColumns || []);
        setColumns(result);
      } catch (err) {
        // notif(err, "error");
      }
    }
  }, [sheet, headerRow, fileName]);

  const handleUpload = async (info: UploadedFileInfo) => {
    setOriginalName(info.originalname);
    setFileName(info.name);
    setExclude(false);
    setSheet("");
    setHeaderRow("1");
    setDataRow("2");
    setColumnSKU("");
    setTokenColumns([]);
    setImportAction(ImportProductsArgsAction.None);
    if (isExcel(info.extension)) {
      const result = await api.file.sheets(info.name);
      setSheets(result);
    } else {
      setSheets([]);
    }
  };

  useEffect(() => {
    if (action !== "none" && retailer !== null) {
      setOriginalName("<none>");
      setFileName("");
      setExclude(false);
      setSheet("");
      setHeaderRow("1");
      setSheets([]);
      setColumns([]);
      setColumnSKU("");
      setTokenColumns([]);
      setImportAction(ImportProductsArgsAction.None);
      setOpen(true);
    }
  }, [action, retailer]);

  useEffect(() => {
    loadColumns();
  }, [sheet, headerRow, fileName]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl">
      <DialogTitle>{action.toUpperCase()} PRODUCTS</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} direction="row" alignItems="top">
          <FormGroup>
            <Typography>Retailer: {retailer?.name}</Typography>
            <Divider sx={{ paddingTop: 10 }} />
            <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
              <FormGroup>
                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography>File:</Typography>
                  <Typography>{originalName}</Typography>
                  <UploadFileControl accept={["csv", "xls", "xlsx"]} onUploaded={(e) => handleUpload(e)} />
                </Stack>
              </FormGroup>
            </FormControl>
            {sheets.length > 0 && (
              <FormGroup>
                <br />
                <FormControl>
                  <InputLabel id="sheet-select-label">Sheet</InputLabel>
                  <Select labelId="sheet-select-label" label="Sheet" value={sheet} onChange={(e) => setSheet(e.target.value)}>
                    {sheets?.map((sheet, index) => {
                      return (
                        <MenuItem key={index} value={sheet}>
                          {sheet}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField label="Header row" variant="standard" type="number" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} />
                <TextField label="Data row" variant="standard" type="number" value={dataRow} onChange={(e) => setDataRow(e.target.value)} />
              </FormGroup>
            )}
            {columns.length > 0 && (
              <FormGroup>
                <FormGroup sx={{ paddingTop: 6 }}>
                  <ColumnList label="Column SKU" columns={columns} value={columnSKU} onChange={(value) => setColumnSKU(value)} />
                </FormGroup>
              </FormGroup>
            )}
            <FormControlLabel label="Exclude" control={<Checkbox checked={exclude} onChange={(e, c) => setExclude(c)} />} />
          </FormGroup>
          {action === "import" && columns.length > 0 && <ColumnMultiList columns={columns} values={tokenColumns} onChange={(values) => setTokenColumns(values)} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>{firstLetterUppercase(action)}</Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
